import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { Download, FileSpreadsheet, FileType, FileText } from 'lucide-react';
import { useDebounce } from '../../hooks/useDebounce';
import { Button, Modal, message, cn } from '../../ui';
import { previewPdf } from '../../lib/pdf';
import { request, api } from '../../api/client';
import { usePermission } from '../../store/auth';
import { categoryMeta, visibleOptions, type ReportDefinition } from './reportKit';
import { useReportUrlState, DEFAULT_FROM, DEFAULT_TO } from './useReportUrlState';
import { splitFilters, isSetValue, pushRecent } from './reportPresentation';
import { useFillHeight } from './useFillHeight';
import { ReportsHome } from './ReportsHome';
import { ReportNavigator } from './ReportNavigator';
import { ReportWorkspaceHeader } from './ReportWorkspaceHeader';
import { ReportParameterBar } from './ReportParameterBar';
import { ActiveFilterChips } from './ActiveFilterChips';
import { ReportResultArea } from './ReportResultArea';

type Format = 'pdf' | 'xlsx' | 'csv';

const MIME: Record<Format, string> = {
  pdf: 'application/pdf',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  csv: 'text/csv;charset=utf-8',
};
const FORMAT_LABEL: Record<Format, string> = { pdf: 'PDF', xlsx: 'Excel', csv: 'CSV' };

/**
 * The reporting console.
 *
 * Two screens behind one route. `/reports` is the catalogue — search first, every report two scrolls
 * away. `/reports?r=CODE` is the workspace: a compact header, one band of parameters, and then the
 * report, which gets every pixel that is left.
 *
 * This component owns the STATE (what is selected, what it is filtered by, what a run returned) and
 * the three things that talk to the server — the catalogue, the run, the export. Each piece of the
 * screen is its own component underneath. What reaches the API is exactly what it always was: the
 * same `POST /reports/:code/run` body, the same `run.pdf|xlsx|csv`, the same `f.`-prefixed URL.
 */
export function ReportsPage() {
  const {
    code, area, from, to, filters, kpiSort,
    setCode, setArea, setRange, patchFilters, clearFilters, setKpiSort,
  } = useReportUrlState();

  const [busy, setBusy] = useState<Format | null>(null);
  const [navigatorOpen, setNavigatorOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);

  /*
   * Mirrors the server's gate on the six export routes (`REPORT.RUN` AND `REPORT.EXPORT`, owner
   * decision 16 Sep). Running a report is `REPORT.RUN` and is untouched — reaching this screen at all
   * proves the reader holds it. Without the export grant the dialog must never open: offering three
   * download buttons the API answers with a 403 is a screen that lies about what the reader may do.
   */
  const canExport = usePermission('REPORT.EXPORT');

  const { data: catalog, isLoading: catalogLoading } = useQuery({
    queryKey: ['reports-catalog'],
    queryFn: () => request<ReportDefinition[]>({ url: '/reports' }),
  });

  const reports = useMemo(() => catalog ?? [], [catalog]);
  const selected = reports.find((r) => r.code === code);
  const meta = categoryMeta(selected?.category ?? '');

  /** Filters a report declares, with any `default` filled in so the control opens where the SQL will. */
  const effectiveFilters = useMemo(() => {
    const out: Record<string, any> = { ...filters };
    for (const f of selected?.filters ?? []) {
      if (f.default !== undefined && out[f.key] === undefined) out[f.key] = f.default;
    }
    return out;
  }, [filters, selected]);

  // Every filter on the bar, the most-used first — there is no drawer any more.
  const barFilters = useMemo(() => {
    const { primary, advanced } = splitFilters(selected);
    return [...primary, ...advanced];
  }, [selected]);
  const activeFilterCount = (selected?.filters ?? []).filter((f) => isSetValue(filters[f.key])).length;

  /**
   * CHANGING A FILTER MUST NOT LEAVE A CONTRADICTION BEHIND IT.
   *
   * The filters are ANDed in the SQL, so "Retailer: Shree Ganesh Krishi Kendra" AND "Territory: Delhi"
   * is not a narrower report, it is an impossible one — and it ran anyway, printing a header that
   * claimed both while the rows could only ever satisfy one. Applying a change now drops any dependent
   * choice the new parent can no longer offer, says which one it dropped, and writes the whole change
   * as ONE url patch so the two cannot land out of order.
   *
   * Takes a MAP rather than a single key, so a change that settles several keys at once (a drill, a
   * cascade) goes to the URL as one write.
   */
  const applyFilterChanges = useCallback((changes: Record<string, any>) => {
    const next: Record<string, any> = { ...effectiveFilters };
    for (const [k, v] of Object.entries(changes)) {
      if (v === undefined || v === null || v === '') delete next[k]; else next[k] = v;
    }
    const write: Record<string, any> = { ...changes };
    const dropped: string[] = [];
    // A chain (territory → rep, group → product) settles in a pass or two; loop until nothing falls.
    for (let pass = 0; pass < 4; pass++) {
      let changed = false;
      for (const f of selected?.filters ?? []) {
        if (!f.dependsOn) continue;
        const cur = next[f.key];
        if (!isSetValue(cur)) continue;
        const allowed = new Set(visibleOptions(f, next).map((o) => String(o.value)));
        const had = Array.isArray(cur) ? cur.map(String) : [String(cur)];
        const kept = had.filter((v) => allowed.has(v));
        if (kept.length === had.length) continue;
        // A multi-select keeps the choices that survive; a single one is simply cleared.
        const keptValue = Array.isArray(cur) ? (kept.length ? kept : undefined) : undefined;
        if (keptValue === undefined) delete next[f.key]; else next[f.key] = keptValue;
        write[f.key] = keptValue;
        dropped.push(f.label);
        changed = true;
      }
      if (!changed) break;
    }
    if (dropped.length) {
      const parentKey = Object.keys(changes)[0];
      const parent = (selected?.filters ?? []).find((f) => f.key === parentKey);
      message.info(`${dropped.join(' and ')} cleared — no longer inside the ${(parent?.label ?? parentKey).toLowerCase()} you picked.`);
    }
    patchFilters(write);
    return next;
  }, [effectiveFilters, selected, patchFilters]);

  const validRange = !from || !to || from <= to;
  const canRun = !!code && validRange;

  /*
   * THE REPORT RUNS ITSELF.
   *
   * Opening a report used to land on "Ready when you are" and a Run button, and every change of
   * period or filter needed another press plus a Yes/No — so reading a report was a chore of
   * clicking. A run only READS, so it is not under the confirm-before-changing-data rule. It is now a
   * query on the parameters themselves: it runs when the report opens and again whenever the period
   * or a filter changes.
   *
   * Parameters are debounced so typing "100" into a number filter runs one query, not three. The
   * previous rows stay on screen while a re-run loads, but only for the SAME report: another report's
   * columns under this one's heading would be a lie about what was run.
   *
   * A report with a forward period waits for the effect below to move the range forward, rather than
   * running once on the backward default and then again.
   */
  const params = useDebounce(useMemo(() => ({ from, to, ...effectiveFilters }), [from, to, effectiveFilters]), 350);
  const awaitingForwardPeriod = !!selected?.periodAhead && from === DEFAULT_FROM() && to === DEFAULT_TO();
  const run = useQuery({
    queryKey: ['report-run', code, params],
    queryFn: () => request<any>({ url: `/reports/${code}/run`, method: 'POST', data: params }),
    enabled: !!selected && canRun && !awaitingForwardPeriod,
    placeholderData: (prev, prevQuery) => (prevQuery?.queryKey[1] === code ? prev : undefined),
    retry: false,
    refetchOnWindowFocus: false,
  });

  /**
   * A summary tile opened (or closed) its own slice: record the filter in the URL — so the drill is
   * shareable and the Back button undoes it — and the query re-runs on the new value by itself.
   */
  const onDrill = useCallback((key: string, value: string | string[] | undefined) => {
    applyFilterChanges({ [key]: value });
  }, [applyFilterChanges]);

  // The report list this browser has been using, for the catalogue's "Recently used" row.
  useEffect(() => { if (code && selected) pushRecent(code); }, [code, selected]);

  /*
   * A report whose period runs FORWARD opens on a forward window.
   *
   * Expiry Alert filters on the expiry date, so arriving with the app-wide "last 30 days" would show
   * an empty report every time — and an empty report reads as "nothing is expiring", which is the
   * opposite of what it is for. Only nudged while the range is still the untouched default, so it
   * never overrides a period the reader chose.
   */
  useEffect(() => {
    if (!selected?.periodAhead) return;
    if (from !== DEFAULT_FROM() || to !== DEFAULT_TO()) return;
    setRange(dayjs().format('YYYY-MM-DD'), dayjs().add(180, 'day').format('YYYY-MM-DD'));
  }, [selected?.code, selected?.periodAhead, from, to, setRange]);

  const periodLabel = selected?.pointInTime
    ? 'as of today'
    : `${dayjs(from).format('DD MMM')} – ${dayjs(to).format('DD MMM YYYY')}`;

  /* ── export ─────────────────────────────────────────────────────────────────────────────────── */

  /**
   * Show the PDF rather than pushing it into Downloads.
   *
   * The app's convention everywhere else: a document opens in the browser's own viewer, which has a
   * download button of its own — so nobody ends up with six near-identical files from checking
   * whether they picked the right period.
   */
  const preview = async () => {
    if (!code || !canExport) return;
    setBusy('pdf');
    try {
      await previewPdf(() => api.post(
        `/reports/${code}/run.pdf`, { from, to, ...effectiveFilters }, { responseType: 'blob' },
      ));
      setExportOpen(false);
    } catch {
      message.error('Could not open the report preview.');
    } finally {
      setBusy(null);
    }
  };

  const download = async (format: Format) => {
    if (!code || !canExport) return;
    setBusy(format);
    try {
      const res = await api.post(`/reports/${code}/run.${format}`, { from, to, ...effectiveFilters }, { responseType: 'blob' });
      // Use the server's filename (GreenBay_Report_Period.ext) — the old code saved it as the raw report
      // CODE, so downloads were named things like "daily_sales_summary.xlsx" with no period on them.
      const disp = String(res.headers?.['content-disposition'] ?? '');
      const served = /filename="([^"]+)"/.exec(disp)?.[1];
      const rows = Number(res.headers?.['x-export-rows']);
      const cut = String(res.headers?.['x-export-truncated'] ?? '') === 'true';
      const url = URL.createObjectURL(new Blob([res.data], { type: MIME[format] }));
      const a = document.createElement('a');
      a.href = url;
      a.download = served || `${code}.${format}`;
      document.body.appendChild(a);
      a.click();
      // Revoke on a timer, not immediately: revoking on the next line can cancel the download in Chrome.
      setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 100);
      const toast = cut ? message.warning : message.success;
      toast(
        [`${FORMAT_LABEL[format]} downloaded`,
          Number.isFinite(rows) ? `${rows.toLocaleString('en-IN')} row${rows === 1 ? '' : 's'}` : null,
          cut ? 'truncated at the export limit — narrow the period for a complete file' : null,
          selected?.pointInTime ? null : `${dayjs(from).format('DD MMM')} – ${dayjs(to).format('DD MMM YYYY')}`,
        ].filter(Boolean).join(' · ') + (served ? ` — ${served}` : ''),
      );
    } catch (e: any) {
      let msg = 'Could not download the report.';
      const body = e?.response?.data;
      if (body instanceof Blob) {
        try { msg = JSON.parse(await body.text())?.error?.message ?? msg; } catch { /* keep default */ }
      } else if (body?.error?.message) { msg = body.error.message; }
      message.error(msg);
    } finally {
      setBusy(null);
      setExportOpen(false);
    }
  };

  /* ── layout ─────────────────────────────────────────────────────────────────────────────────── */

  const [frame, setFrame] = useState<HTMLDivElement | null>(null);
  const frameRef = useCallback((node: HTMLDivElement | null) => setFrame(node), []);
  const frameHeight = useFillHeight(frame);

  const openReport = (c: string) => { setCode(c); setNavigatorOpen(false); };

  if (!selected) {
    return (
      <>
        <ReportsHome
          reports={reports}
          loading={catalogLoading}
          area={area}
          onArea={setArea}
          onPick={openReport}
        />
        <ReportNavigator
          open={navigatorOpen}
          onClose={() => setNavigatorOpen(false)}
          reports={reports}
          current={code}
          onPick={openReport}
          loading={catalogLoading}
        />
      </>
    );
  }

  return (
    /* The frame: a fixed-height column on a desktop (so the table owns the rest of the screen) and an
       ordinary flowing page below `lg`, where a fixed height is how a phone ends up with a table that
       cannot be reached. The negative margin gives back the shell's bottom padding, which would
       otherwise put ~96px of nothing under a frame that is already the height of the viewport. */
    <div
      ref={frameRef}
      className={cn('flex min-w-0 flex-col lg:-mb-20', frameHeight ? 'min-h-0' : 'min-h-[70vh]')}
      style={frameHeight ? { height: frameHeight } : undefined}
    >
      <ReportWorkspaceHeader
        report={selected}
        onHome={() => setCode(undefined)}
        onArea={(a) => { setArea(a); setCode(undefined); }}
        onChangeReport={() => setNavigatorOpen(true)}
        onRun={() => run.refetch()}
        running={run.isFetching}
        canRun={canRun}
        onExport={() => setExportOpen(true)}
        exporting={!!busy}
        canExport={canExport}
      />

      <ReportParameterBar
        report={selected}
        from={from}
        to={to}
        onRange={setRange}
        filters={barFilters}
        values={effectiveFilters}
        onFilterChange={(key, value) => applyFilterChanges({ [key]: value })}
        onReset={clearFilters}
        canReset={activeFilterCount > 0}
        validRange={validRange}
      />

      <ActiveFilterChips
        filters={selected.filters ?? []}
        values={filters}
        onRemove={(key) => applyFilterChanges({ [key]: undefined })}
        onClear={clearFilters}
      />

      <ReportResultArea
        className="mt-1"
        report={selected}
        code={selected.code}
        tone={meta.tone}
        data={run.data}
        isPending={run.isLoading}
        isError={run.isError}
        error={run.error}
        onRun={() => run.refetch()}
        running={run.isFetching}
        periodLabel={periodLabel}
        kpiSort={kpiSort}
        onSort={setKpiSort}
        filters={effectiveFilters}
        onDrill={onDrill}
        activeFilterCount={activeFilterCount}
        onClearFilters={clearFilters}
      />

      <ReportNavigator
        open={navigatorOpen}
        onClose={() => setNavigatorOpen(false)}
        reports={reports}
        current={code}
        onPick={openReport}
        loading={catalogLoading}
      />

      {/* The export dialog — the ONLY place the three formats are offered; the header's Export button
          opens it. It restates the period, the filters and the row count before writing a file, which
          is what makes an exported document traceable. */}
      <Modal
        open={exportOpen && canExport}
        onOpenChange={setExportOpen}
        width={560}
        title={
          <span className="flex items-center gap-2">
            <span className="grid size-7 place-items-center rounded-lg bg-brand-100 text-brand-700 dark:bg-brand-500/20 dark:text-brand-300" aria-hidden>
              <Download className="size-4" />
            </span>
            Export {selected.name}
          </span>
        }
      >
        <div className="space-y-4">
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 rounded-card border border-line bg-surface-2 p-3 text-sm">
            <dt className="type-label-strong text-subtle">Period</dt>
            <dd className="text-content">{periodLabel}</dd>
            <dt className="type-label-strong text-subtle">Filters</dt>
            <dd className="text-content">
              {/* The file is written with effectiveFilters, defaults included — so is this line. Counting only
                  the URL said "None — the whole set" over a Payment Register filtered to APPROVED. */}
              {(() => {
                const applied = (selected.filters ?? []).filter((f) => isSetValue(effectiveFilters[f.key]));
                return applied.length
                  ? applied.map((f) => (isSetValue(filters[f.key]) ? f.label : `${f.label} (default)`)).join(', ')
                  : 'None — the whole set';
              })()}
            </dd>
            <dt className="type-label-strong text-subtle">Rows</dt>
            <dd className="text-content">
              {run.data
                ? `${(run.data.rows?.length ?? 0).toLocaleString('en-IN')} on screen${run.data.truncated ? ' (capped — the file has the full list)' : ''}`
                : 'Still loading — the file is generated fresh either way'}
            </dd>
          </dl>

          <div className="space-y-2">
            {[
              { fmt: 'pdf' as Format, icon: <FileType className="size-4" />, title: 'PDF', blurb: 'Opens in a new tab to read first — the letterheaded document, with a totals row.', action: preview, cta: 'Preview' },
              { fmt: 'xlsx' as Format, icon: <FileSpreadsheet className="size-4" />, title: 'Excel', blurb: 'Formatted workbook: real numbers you can sum, frozen header, filters, totals row.', action: () => download('xlsx'), cta: 'Download' },
              { fmt: 'csv' as Format, icon: <FileText className="size-4" />, title: 'CSV', blurb: 'Raw data for a pivot table, Tally or a script — no letterhead, no totals row.', action: () => download('csv'), cta: 'Download' },
            ].map((o) => (
              <div key={o.fmt} className="flex items-center gap-3 rounded-card border border-line p-3">
                <span className="grid size-9 shrink-0 place-items-center rounded-lg bg-surface-2 text-muted" aria-hidden>{o.icon}</span>
                <div className="min-w-0 flex-1">
                  <p className="type-body-strong text-content">{o.title}</p>
                  <p className="type-caption text-muted">{o.blurb}</p>
                </div>
                <Button
                  variant={o.fmt === 'pdf' ? 'primary' : 'default'}
                  size="sm"
                  loading={busy === o.fmt}
                  disabled={!!busy}
                  onClick={o.action}
                >
                  {o.cta}
                </Button>
              </div>
            ))}
          </div>

          <p className="type-caption text-subtle">
            Every format is generated from the same query at the full export limit, so a capped
            screen still exports the complete list.
          </p>
        </div>
      </Modal>
    </div>
  );
}
